import { useState, useMemo, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  IconButton,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid2 as Grid,
  CardContent,
  CardActions,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Close as CloseIcon,
  Public as PublicIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import PaymentIconComponent from '../ui/PaymentIcon';
import { usePayments } from '../../hooks/queries';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useDialog } from '../../hooks/useDialog';
import { useQueryClient } from '@tanstack/react-query';
import CascadeDeleteDialog from '../ui/CascadeDeleteDialog';
import StandardCard from '../ui/StandardCard';
import AppSnackbar from '../ui/AppSnackbar';
import { getPaymentTypeIcon, getPaymentTypeLabel, PAYMENT_TYPE_LABELS } from '../../utils/paymentTypeIcons';

// Payment types - DEVE corresponder ao enum PaymentType do backend
const PaymentMethodTypes = Object.fromEntries(
  Object.entries(PAYMENT_TYPE_LABELS).map(([value, label]) => [label, { value: Number(value), label }])
);

const getPaymentIcon = (typeValue) => getPaymentTypeIcon(typeValue);

/**
 * PaymentMethodsManagerDialog
 *
 * Componente de diálogo reutilizável que gerencia formas de pagamento.
 * Replica 100% da funcionalidade da antiga página de formas de pagamento em uma modal.
 *
 * @param {boolean} open - Se o dialog está aberto
 * @param {function} onClose - Callback ao fechar o dialog
 * @param {string} title - Título do dialog (opcional - padrão: "Gerenciar Formas de Pagamento")
 *
 * @example
 * const [open, setOpen] = useState(false);
 * <PaymentMethodsManagerDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   title="Gerenciar Formas de Pagamento"
 * />
 */
export default function PaymentMethodsManagerDialog({
  open,
  onClose,
  title = 'Gerenciar Formas de Pagamento'
}) {
  const { items: rawItems, loading, createAsync, updateAsync, reload } = usePayments();
  const queryClient = useQueryClient();
  const paymentMethods = useMemo(
    () => [...(rawItems || [])].sort((a, b) => {
      if (a.isGlobal === b.isGlobal) return 0;
      return a.isGlobal ? 1 : -1;
    }),
    [rawItems]
  );
  const editDialog = useDialog();
  const deleteDialog = useDialog();
  const [formData, setFormData] = useState({
    type: 1, // Default: Cash
    name: '',
    description: '',
    icon: '',
  });
  const { snackbar, showSuccess, showError, showWarning, hideSnackbar } = useSnackbar();

  // Resetar showAllPayments quando o dialog abre/fecha
  useEffect(() => {
    if (!open) {
      editDialog.handleClose();
      deleteDialog.handleClose();
    }
  }, [open]);

  const handleOpenDialog = (paymentMethod = null) => {
    if (paymentMethod) {
      // Apenas payment methods customizados podem ser editados
      if (paymentMethod.isGlobal) {
        showWarning('Formas de pagamento globais não podem ser editadas');
        return;
      }
      setFormData({
        type: paymentMethod.type,
        name: paymentMethod.name,
        description: paymentMethod.description || '',
        icon: paymentMethod.icon || '',
      });
    } else {
      setFormData({
        type: 1, // Default: Cash
        name: '',
        description: '',
        icon: '',
      });
    }
    editDialog.handleOpen(paymentMethod);
  };

  const handleCloseDialog = () => {
    editDialog.handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'type' ? parseInt(value) : value,
    });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        type: parseInt(formData.type),
        name: formData.name,
        description: formData.description || null,
        icon: formData.icon || null,
      };

      if (editDialog.isEdit) {
        await updateAsync(editDialog.data.id, payload);
        showSuccess('Método de pagamento atualizado com sucesso!');
      } else {
        await createAsync(payload);
        showSuccess('Método de pagamento criado com sucesso!');
      }
      // Invalidar cache de formas de pagamento
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      handleCloseDialog();
    } catch (error) {
      showError('Erro ao salvar: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleOpenDeleteDialog = (paymentMethod) => {
    // Não permitir deletar payment methods globais
    if (paymentMethod.isGlobal) {
      showWarning('Formas de pagamento globais não podem ser excluídas');
      return;
    }
    deleteDialog.handleOpen(paymentMethod);
  };

  const handleCloseDeleteDialog = () => {
    deleteDialog.handleClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '500px' } }}
      >
        {/* Dialog Title com fechar X */}
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <PaymentIcon color="primary" />
          <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <Tooltip title="Fechar">
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        {/* Dialog Content - Grid de cards */}
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : paymentMethods.length === 0 ? (
            <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed', borderColor: 'divider' }}>
              <PaymentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Nenhum método de pagamento encontrado
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Crie métodos customizados para seus serviços
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                Criar Primeiro Método
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {paymentMethods.map((pm) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={pm.id}>
                  <StandardCard>
                    <CardContent sx={{ flexGrow: 1, p: 2, '&:last-child': { pb: 2 } }}>
                      {/* Header compacto */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        {pm.isGlobal ? (
                          // Para pagamentos globais, usar sprite de ícones
                          <PaymentIconComponent
                            displayOrder={pm.displayOrder || 1}
                            size={40}
                          />
                        ) : (
                          // Para pagamentos customizados, usar ícone MUI
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'primary.main',
                              color: 'white',
                              fontSize: '1.25rem'
                            }}
                          >
                            {getPaymentIcon(pm.type)}
                          </Box>
                        )}
                        <Box flexGrow={1} sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle1" component="div" noWrap sx={{ fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.3 }}>
                            {pm.name}
                          </Typography>
                          {pm.description && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                              {pm.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Badges compactos */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {pm.isGlobal ? (
                          <Chip
                            icon={<PublicIcon sx={{ fontSize: '0.875rem' }} />}
                            label="Somente Leitura"
                            size="small"
                            color="default"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-icon': { ml: 0.5 } }}
                          />
                        ) : (
                          <Chip
                            icon={<PersonIcon sx={{ fontSize: '0.875rem' }} />}
                            label="Customizado"
                            size="small"
                            color="secondary"
                            sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-icon': { ml: 0.5 } }}
                          />
                        )}
                      </Box>
                    </CardContent>

                    {/* Ações compactas */}
                    {!pm.isGlobal && (
                      <CardActions sx={{ justifyContent: 'flex-end', p: 1, pt: 0 }}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(pm)}
                            sx={{ p: 0.5 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(pm)}
                            sx={{ p: 0.5 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    )}
                  </StandardCard>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Novo Método Customizado
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Criar/Editar - aninhado */}
      <Dialog open={editDialog.open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editDialog.isEdit ? 'Editar Método de Pagamento' : 'Novo Método de Pagamento'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    label="Tipo"
                  >
                    {Object.entries(PaymentMethodTypes).map(([key, type]) => (
                      <MenuItem key={key} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Ícone"
                  name="icon"
                  value={formData.icon}
                  onChange={handleChange}
                  fullWidth
                  placeholder="Ex: 💳, 💰, 🏥"
                  helperText="Emoji ou ícone para representar este método"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Nome"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  fullWidth
                  placeholder="Ex: Unimed Curitiba, Plano XYZ"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Descrição"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Informações adicionais sobre este método de pagamento"
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Este método ficará disponível apenas para você vincular aos seus serviços.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão - aninhado */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        onDeleted={() => {
          handleCloseDeleteDialog();
          showSuccess('Excluído com sucesso!');
          queryClient.invalidateQueries({ queryKey: ['payments'] });
          reload();
        }}
        deletePath={`/payments/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} autoHideDuration={6000} />
    </>
  );
}
